// SearchResultCard — composant UNIVERSEL de résultat AYA Search (v5.13.0).
// PHOTO + FICHE STRUCTURÉE pour toutes les catégories, data-driven :
// la catégorie détermine la variante (photo en haut / photo à gauche) et les infos,
// la structure visuelle AYA reste commune. RÈGLE : aucune action sans capacité réelle.
import { MapPin, Phone, Navigation, Share2, ExternalLink, Clock } from "lucide-react";
import { toast } from "sonner";
import { openWhatsAppOrder } from "@/lib/whatsappOrder";

// Catégories où la photo est essentielle → pleine largeur en haut.
const HERO_CATS = new Set(["hotel", "restaurant", "divertissement", "commerce"]);
// Emoji de secours par catégorie (fallback élégant, jamais d'image hors-sujet).
const FALLBACK_EMOJI = {
  hotel: "🏨", restaurant: "🍽", commerce: "🛍", coiffure: "💈", beaute: "💅",
  pharmacie: "💊", sante: "🩺", justice: "⚖️", finance: "🏦", administration: "🏛",
  divertissement: "🎉", transport: "🚕", services: "🛠", emploi: "💼",
};

const C = {
  card: { borderRadius: 14, background: "rgba(8,38,24,0.75)", border: "1px solid rgba(16,185,129,0.22)", overflow: "hidden" },
  badge: { display: "inline-block", padding: "3px 9px", borderRadius: 999, fontSize: 9, fontWeight: 800, letterSpacing: 0.7, height: "fit-content" },
  btn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "7px 12px", borderRadius: 9, border: "1px solid rgba(16,185,129,0.35)", background: "transparent", color: "#CFE5D6", fontSize: 11.5, cursor: "pointer", textDecoration: "none" },
  meta: { color: "#9DB4A6", fontSize: 12, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" },
  gold: { color: "#EFC75E", fontSize: 12, fontWeight: 800 },
  heroImg: { width: "100%", height: 150, objectFit: "cover", display: "block", background: "#06281A" },
  sideImg: { width: 102, minHeight: 102, alignSelf: "stretch", objectFit: "cover", display: "block", background: "#06281A", flexShrink: 0 },
  fallback: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, background: "radial-gradient(120% 140% at 20% 0%, rgba(16,185,129,0.22), rgba(6,40,26,0.92) 60%, #04120B 100%)" },
};

const Fallback = ({ r, hero }) => (
  <div style={{ ...C.fallback, ...(hero ? { height: 110 } : { width: 102, alignSelf: "stretch", flexShrink: 0 }) }} data-testid={`result-img-fallback-${r.id}`}>
    <span style={{ fontSize: hero ? 30 : 24 }}>{r.category_emoji || FALLBACK_EMOJI[r.category] || "📍"}</span>
    <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 1.2, color: "rgba(52,211,153,0.6)" }}>AYA</span>
  </div>
);

// Infos spécifiques par catégorie — uniquement des données réellement présentes.
const CategoryInfo = ({ r }) => {
  const x = r.extra || {};
  switch (r.category) {
    case "emploi":
      return (
        <div style={{ display: "grid", gap: 2 }}>
          {x.company && <div style={{ color: "#CFE5D6", fontSize: 12.5, fontWeight: 700 }}>🏢 {x.company}</div>}
          <div style={C.meta}>
            {x.contract_type && <span style={{ ...C.badge, background: "rgba(59,130,246,0.2)", color: "#93C5FD" }}>{x.contract_type}</span>}
            {x.experience_level && <span>{x.experience_level}</span>}
          </div>
          {x.salary_display && <div style={C.gold}>💰 {x.salary_display}</div>}
        </div>
      );
    case "administration":
      return (
        <div style={{ display: "grid", gap: 2 }}>
          {x.organism && <div style={{ color: "#CFE5D6", fontSize: 12.5 }}>{x.organism}</div>}
          <div style={C.meta}>
            {x.cost_display && <span style={C.gold}>{x.cost_display}</span>}
            {x.duration && <span><Clock size={11} style={{ verticalAlign: -1 }} /> {x.duration}</span>}
          </div>
          {Array.isArray(x.services) && x.services.length > 0 && <div style={{ color: "#9DB4A6", fontSize: 11.5 }}>{x.services.join(" · ")}</div>}
        </div>
      );
    case "finance":
      return <div style={C.meta}>{x.type && <span>{x.type}</span>}{x.rate_display && <span style={C.gold}>📈 {x.rate_display}</span>}</div>;
    case "justice":
      return (
        <div style={{ display: "grid", gap: 2 }}>
          <div style={C.meta}>
            {x.specialite && <span>⚖️ {x.specialite}</span>}
            {x.annees_experience && <span>{x.annees_experience} ans d'expérience</span>}
          </div>
          {x.honoraires && <div style={C.gold}>💰 {x.honoraires}</div>}
        </div>
      );
    case "sante":
    case "pharmacie":
      return <div style={C.meta}>{x.specialite && <span>🩺 {x.specialite}</span>}{x.etablissement && !x.specialite && <span>{x.etablissement}</span>}</div>;
    case "hotel":
      return <div style={C.meta}>{x.stars != null && <span style={C.gold}>{"★".repeat(x.stars)}</span>}{Array.isArray(x.amenities) && x.amenities.length > 0 && <span>{x.amenities.join(" · ")}</span>}</div>;
    case "transport":
      return <div style={C.meta}>{x.type && <span>🚕 {x.type}</span>}{x.availability && <span style={{ color: "#34D399" }}>● {x.availability}</span>}</div>;
    default:
      return Array.isArray(r.specialties) && r.specialties.length > 0
        ? <div style={{ color: "#9DB4A6", fontSize: 11.5 }}>{r.specialties.slice(0, 4).join(" · ")}</div>
        : null;
  }
};

export const SearchResultCard = ({ result: r, navigate }) => {
  const x = r.extra || {};
  const caps = r.capabilities || {};
  const hero = HERO_CATS.has(r.category);
  const hasImg = !!r.image;
  const openDetail = r.link ? () => navigate(r.link) : null;

  const onCall = (e) => { e.stopPropagation(); window.location.href = `tel:${String(r.tel).replace(/\s/g, "")}`; };
  const onRoute = (e) => { e.stopPropagation(); navigate(`/map-explorer?lat=${r.lat}&lng=${r.lng}&label=${encodeURIComponent(r.name)}`); };
  const onShare = async (e) => {
    e.stopPropagation();
    const text = `${r.name}${r.commune_name ? " — " + r.commune_name : ""} (via AYA)`;
    if (navigator.share) { try { await navigator.share({ text }); } catch { /* annulé */ } }
    else { await navigator.clipboard?.writeText(text); toast.success("Copié ✓"); }
  };

  const Photo = () => {
    if (!hasImg && r.kind === "job") return null;
    const style = hero ? C.heroImg : C.sideImg;
    const img = hasImg
      ? <img src={r.image} alt={r.image_alt || r.name} loading="lazy" decoding="async" style={style}
          onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid={`result-img-${r.id}`} />
      : <Fallback r={r} hero={hero} />;
    return openDetail
      ? <div onClick={(e) => { e.stopPropagation(); openDetail(); }} role="button" aria-label={r.name}
          className="aya-news-tap" style={{ cursor: "pointer", display: "flex", flexShrink: 0, ...(hero ? {} : { alignSelf: "stretch" }) }}
          data-testid={`result-photo-open-${r.id}`}>{img}</div>
      : img;
  };

  const body = (
    <div style={{ padding: hero ? "11px 13px 13px" : "11px 12px 12px", flex: 1, minWidth: 0 }}>
      <header style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {r.category_label && (
          <span style={{ ...C.badge, background: "rgba(16,185,129,0.15)", color: "#6EE7B7" }}>
            {r.category_emoji} {String(r.category_label).toUpperCase()}
          </span>
        )}
        {x.garde && <span style={{ ...C.badge, background: "rgba(239,68,68,0.2)", color: "#FCA5A5" }}>🚨 DE GARDE</span>}
        {r.provenance === "partner" && (
          <span style={{ ...C.badge, background: "linear-gradient(135deg, #D4AF37, #B8860B)", color: "#1A1206" }}
            data-testid={`partner-badge-${r.id}`}>★ PARTENAIRE AYA</span>
        )}
        {r.verified && <span style={{ ...C.badge, background: "rgba(16,185,129,0.2)", color: "#34D399" }}>VÉRIFIÉ</span>}
      </header>
      <h3 onClick={openDetail ? (e) => { e.stopPropagation(); openDetail(); } : undefined}
        className={openDetail ? "aya-news-tap" : undefined}
        style={{ margin: "5px 0 3px", fontSize: 15, fontWeight: 800, color: "#ECF5EE", lineHeight: 1.25, cursor: openDetail ? "pointer" : "inherit" }}
        data-testid={`result-title-${r.id}`}>{r.name}</h3>
      <div style={{ ...C.meta, marginBottom: 3 }}>
        {r.rating != null && <span style={C.gold}>⭐ {r.rating}{r.reviews_count ? ` (${r.reviews_count})` : ""}</span>}
        {r.category !== "emploi" && r.commune_name && <span><MapPin size={11} style={{ verticalAlign: -1 }} /> {r.commune_name}</span>}
        {r.distance_km != null && <span>📏 {r.distance_km} km</span>}
        {r.hours && <span><Clock size={11} style={{ verticalAlign: -1 }} /> {r.hours}</span>}
      </div>
      <CategoryInfo r={r} />
      {r.description && !["emploi", "administration"].includes(r.category) && (
        <p style={{ color: "#9DB4A6", fontSize: 12, lineHeight: 1.4, margin: "4px 0 0", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{r.description}</p>
      )}
      {(r.price_display || r.price_from != null) && (
        <div style={{ ...C.gold, marginTop: 4, fontSize: 12.5 }}>
          {r.price_display || `dès ${Number(r.price_from).toLocaleString("fr-FR")} F`}
        </div>
      )}

      {/* Actions — uniquement si la capacité existe réellement */}
      <div style={{ display: "flex", gap: 7, marginTop: 9, flexWrap: "wrap" }}>
        {caps.pro_access && (
          <button onClick={(e) => { e.stopPropagation(); navigate("/pro/acces"); }} data-testid={`result-pro-access-${r.id}`}
            style={{ ...C.btn, color: "#1A1206", background: "linear-gradient(135deg, #EFC75E, #D4AF37)", border: "none", fontWeight: 800 }}>💼 S'inscrire / Se connecter</button>
        )}
        {r.cabinet_slug && (
          <button onClick={(e) => {
              e.stopPropagation();
              const q = new URLSearchParams(window.location.search).get("q") || "";
              navigate(`/cabinet/${r.cabinet_slug}?src=aya_search${q ? `&q=${encodeURIComponent(q)}` : ""}`);
            }} data-testid={`result-enter-cabinet-${r.id}`}
            style={{ ...C.btn, color: "#04120B", background: "#10B981", border: "none", fontWeight: 800 }}>🏛 Entrer dans le cabinet</button>
        )}
        {caps.booking && r.link && (
          <button onClick={(e) => { e.stopPropagation(); navigate(r.link); }} data-testid={`result-book-${r.id}`}
            style={{ ...C.btn, color: "#04120B", background: "#F97316", border: "none", fontWeight: 800 }}>📅 Réserver</button>
        )}
        {caps.apply && x.apply_url && (
          <a href={x.apply_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`result-apply-${r.id}`}
            style={{ ...C.btn, color: "#34D399", fontWeight: 800 }}>📩 Postuler{x.platform_name ? ` · ${x.platform_name}` : ""}</a>
        )}
        {caps.view && r.kind === "merchant" && r.link && (
          <button onClick={(e) => { e.stopPropagation(); navigate(r.link); }} data-testid={`result-shop-${r.id}`}
            style={{ ...C.btn, color: "#34D399", fontWeight: 800 }}>🏪 Voir la boutique</button>
        )}
        {caps.view && r.kind !== "merchant" && (x.online_url || x.website) && (
          <a href={x.online_url || x.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`result-view-${r.id}`}
            style={{ ...C.btn, color: "#93C5FD" }}><ExternalLink size={12} /> {x.online_url ? "Démarche en ligne" : "Site web"}</a>
        )}
        {caps.whatsapp && r.slug && (
          <button onClick={(e) => { e.stopPropagation(); openWhatsAppOrder({ merchantSlug: r.slug }); }} data-testid={`result-whatsapp-${r.id}`}
            style={{ ...C.btn, color: "#25D366", borderColor: "rgba(37,211,102,0.45)" }}>💬 WhatsApp</button>
        )}
        {caps.call && r.tel && (
          <button onClick={onCall} data-testid={`result-call-${r.id}`} style={{ ...C.btn, color: "#34D399" }}><Phone size={12} /> Appeler</button>
        )}
        {caps.directions && r.lat != null && r.lng != null && (
          <button onClick={onRoute} data-testid={`result-route-${r.id}`} style={{ ...C.btn, color: "#93C5FD" }}><Navigation size={12} /> Itinéraire</button>
        )}
        <button onClick={onShare} data-testid={`result-share-${r.id}`} style={C.btn}><Share2 size={12} /> Partager</button>
      </div>
    </div>
  );

  return (
    <article style={{ ...C.card, ...(hero ? {} : { display: "flex", alignItems: "stretch" }) }} data-testid={`search-result-card-${r.id}`}>
      <Photo />
      {body}
    </article>
  );
};

export default SearchResultCard;
